import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Breadcrumb } from 'react-breadcrumbs'
import { withRouter } from 'react-router-dom'

import { Breadcrumbs } from '../../../Components/Breadcrumbs'
import { CreateCampaignWrapper } from '../../../Components/Campaigns/CreateEditCampaign/CreateEditCampaignWrapper'
import { LayoutDashboard } from '../../../Components/Layout/Dashboard'
import { PageTitle } from '../../../Components/PageTitle'

import { CreateEditCampaignForm } from '../../Form/Campaigns/CreateEditCampaign'
import PreBuild from '../../Form/Campaigns/CreateEditCampaign/PreBuild'
import { HideWhenBlocked } from '../../UIBlock/HideWhenBlocked'

import {
  createCampaign,
  updateCampaign,
  getCampaignById,
} from '../../../Reducers/Campaigns/actions'

import { getClients } from '../../../Reducers/Clients/actions'
import { uploadPitchFile } from '../../../Reducers/Upload/actions'

import CampaignsIcon from '../../../Assets/Images/icons/campaigns'

import { promisify } from '../../../Utils/Actions'

import { PATHS } from '../../../Config/Routing/paths'
import {
  parseCreateCampaignFormValues,
  parseUpdateCampaignFormValues,
  getInitialFormValues,
} from './util'
import {
  runCreateCampaignSideEffects,
  runUpdateCampaignSideEffects,
} from './sideEffects'
import { CAMPAIGN_ENUM_TYPES } from '../../../shared/constants'
import { showReduxModal } from '../../../Reducers/UI/actions'

const CreateCampaign = props => {
  const { clients, editMode } = props
  const title = editMode ? 'Edit Campaign' : 'Create Campaign'

  const [campaign, setCampaign] = useState(null)
  const [loadingData, setLoadingData] = useState({
    isLoading: false,
    label: 'Loading...',
  })

  var [metrics, setMetrics] = useState([])
  var [primaryMetricIndex, setPrimaryMetricIndex] = useState(null)
  var [missions, setMissions] = useState(null)

  useEffect(() => {
    props.getClients()
    if (props.editMode) {
      getCampaignData()
    }
  }, [])

  const getCampaignData = () => {
    setLoadingData({ isLoading: true, label: "Loading campaign's data" })
    props
      .getCampaignById(props.match.params.campaignId, {
        includeMissions: true,
      })
      .then(response => {
        var {
          metrics: metricsRaw,
          cityPlans,
          ...campaign
        } = response.data.campaign
        var missions = cityPlans.nodes.map(c =>
          c.missions.edges.map(edge => ({
            ...edge.node,
            cityPlanId: c.id,
          })),
        )
        setMissions(missions.flat())
        setCampaign(campaign)
        var metrics = metricsRaw.nodes.map((m, i) => {
          var { isPrimary, ...metric } = m
          if (isPrimary) setPrimaryMetricIndex(i)
          return metric
        })
        setMetrics(metrics)
        setLoadingData({ isLoading: false, label: '' })
      })
  }

  const onSubmitHandler = values => {
    // Map in the metrics
    values = Object.assign({}, values)
    values.metrics = metrics
    if (primaryMetricIndex != null)
      values.metrics[primaryMetricIndex].isPrimary = true

    if (values.bonusEnabled && (!values.metrics || !values.metrics.length)) {
      return props.showReduxModal({
        title: 'Validation Error',
        children: (
          <span>
            You cannot create a bonus without creating at least one metric.
          </span>
        ),
        hideCancel: true,
        confirmLabel: 'OK',
      })
    }

    return props.editMode
      ? editCampaignFormSubmitHandler(values)
      : createCampaignFormSubmitHandler(values)
  }

  const createCampaignFormSubmitHandler = async values => {
    setLoadingData({
      isLoading: true,
      label: 'Creating campaign...',
    })
    const { createCampaign } = props
    const parsedValues = parseCreateCampaignFormValues(values)
    try {
      const response = await createCampaign(parsedValues)
      const {
        data: {
          createCampaign: { campaign },
        },
      } = response
      if (campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX) {
        setLoadingData({
          isLoading: true,
          label: 'Creating city plans and missions...',
        })
      }
      await runCreateCampaignSideEffects({
        campaign,
        values,
        props,
      })
      return createCampaignFormSubmitSuccessHandler(campaign.id)
    } catch (error) {
      setLoadingData({
        isLoading: false,
      })
      // Error on this page is always throwed into alert from redux but lets also show error in the console
      console.error(error)
      return
    }
  }

  const createCampaignFormSubmitSuccessHandler = id => {
    const { history } = props
    setLoadingData({
      isLoading: false,
      label: '',
    })
    history.push(
      `${PATHS.main.dashboard}${
        PATHS.dashboard.campaigns
      }/${id}/dashboard/overview`,
    )
  }

  const editCampaignFormSubmitHandler = async values => {
    setLoadingData({
      isLoading: true,
      label: 'Saving updated campaign...',
    })
    const {
      match,
      updateCampaign,
      uploadPitchFile: uploadPitchFileFunction,
    } = props

    const uploadPitchFile = promisify(uploadPitchFileFunction)
    if (values.pitchUrl instanceof Array) {
      const pitchFile = values.pitchUrl[0]
      const pitchUrl = await uploadPitchFile(campaign.id, pitchFile)
      values.pitchUrl = pitchUrl
    } else {
      values.pitchUrl = campaign.pitchUrl
    }

    const parsedValues = parseUpdateCampaignFormValues(
      match.params.campaignId,
      values,
    )
    try {
      await updateCampaign(parsedValues)
      if (campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX) {
        setLoadingData({
          isLoading: true,
          label: 'Creating city plans and missions...',
        })
      }
      await runUpdateCampaignSideEffects({
        campaign,
        values,
        props,
      })
    } catch (error) {
      setLoadingData({
        isLoading: false,
      })
      // Error on this page is always throwed into alert from redux but lets also show error in the console
      console.error(error)
      throw new Error(error)
    }

    return await getCampaignData()
  }

  const loaded = !editMode || (editMode && campaign)
  const formInitialValues = getInitialFormValues(campaign, editMode)

  var CampaignViewComponent = editMode ? CreateEditCampaignForm : PreBuild

  return (
    <LayoutDashboard>
      <HideWhenBlocked>
        <CreateCampaignWrapper>
          <Breadcrumbs icon={<CampaignsIcon style={{ width: 16 }} />} />
          <Breadcrumb
            data={{
              title: 'Campaigns',
              pathname: `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}`,
            }}
          />
          <Breadcrumb
            hidden={!editMode}
            data={{
              title: campaign ? campaign.name : 'Loading name...',
              pathname: `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${
                props.match.params.campaignId
              }${PATHS.campaign.cityPlans}`,
            }}
          />
          <Breadcrumb
            data={{
              title,
              pathname: editMode
                ? `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${
                    props.match.params.campaignId
                  }${PATHS.campaign.cityPlans}/edit` // 'cityPlans' part is added to avoid sorting of breadcrumbs by the internal package code
                : `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${
                    PATHS.campaigns.create
                  }`,
            }}
          />
          <PageTitle className="page-title">{title}</PageTitle>
          {loaded ? (
            <CampaignViewComponent
              {...formInitialValues}
              campaign={campaign}
              cities={props.cities}
              clients={clients}
              onSubmit={onSubmitHandler}
              editMode={editMode}
              initialValues={formInitialValues}
              loading={loadingData.isLoading}
              loadingLabel={loadingData.label}
              canDeletePrimaryMetric={!editMode || !campaign}
              // By pass redux form confusing cludge
              metrics={metrics}
              onMetricUpdate={function(index, metric) {
                metrics = metrics.slice(0)
                metrics[index] = metric
                setMetrics(metrics)
              }}
              onMetricDelete={function(index) {
                metrics = metrics.slice(0)
                metrics.splice(index, 1)
                // If we just deleted out primary then update our primary metric
                // id. We won't do anything fancy like making the first record
                // the primary metric, the server should fail if the user
                // doesn't set a new one
                if (primaryMetricIndex == index) setPrimaryMetricIndex(null)
                setMetrics(metrics)
              }}
              onMetricAdd={function() {
                metrics = metrics.slice(0)
                metrics.push({})
                // If this is the first metric, make it primary
                //if (metrics.length == 1)
                //  setPrimaryMetricIndex(0)
                // Above, don't do that, let's keep it simple, the server should
                // fail if no primary metric is set
                setMetrics(metrics)
              }}
              onPrimaryMetricIndexChange={setPrimaryMetricIndex}
              primaryMetricIndex={primaryMetricIndex}
              missions={missions}
            />
          ) : null}
        </CreateCampaignWrapper>
      </HideWhenBlocked>
    </LayoutDashboard>
  )
}

CreateCampaign.propTypes = {
  clients: PropTypes.array.isRequired,
  createCampaign: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
}

const mapStateToProps = state => ({
  clients: state.clients.clientsList,
  cities: state.city.citiesList,
})

export default connect(
  mapStateToProps,
  {
    createCampaign,
    updateCampaign,
    getClients,
    uploadPitchFile,
    getCampaignById,
    showReduxModal,
  },
)(withRouter(CreateCampaign))



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/CreateEditCampaign/index.js